import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { Divider } from "antd";

// Shared "File Details" sider content for a selected Deliverable card.
// Used by both GlobalDocuments.jsx and AssetDocuments.jsx so the two pages
// can't drift apart the way they did before: AssetDocuments' old "Related
// Assets" block read `selectedCard.equipment_details` / `.tiers_details`,
// fields DeliverableSerializer never actually returns. The real hierarchy
// links (site/plant/equipment/tier/component/instrument) come through the
// polymorphic `links` array instead (DeliverableLinkSerializer: parent_type,
// parent_id, parent_name, plant_name) — that's what this component reads.

const DetailItem = ({ label, value }) => (
  <Box>
    {label && <Typography variant="subtitle2">{label}</Typography>}
    <Typography variant="body1" color="text.secondary">
      {value}
    </Typography>
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography variant="subtitle2" sx={{ color: "primary.main", mb: 1 }}>
    {children}
  </Typography>
);

/**
 * @param {object|null} document - the selected Deliverable (or null for the
 *   "nothing selected" empty state).
 * @param {(link: object) => boolean} [excludeLink] - optional predicate to
 *   hide a link from "Related Assets". Pages scoped to one specific asset
 *   (AssetDocuments viewing an equipment/tier/component's own documents)
 *   use this to hide that same asset linking back to itself; GlobalDocuments
 *   has no "current" asset, so it can omit this prop entirely.
 * @param {string} [title] - sider heading, defaults to "File Details".
 * @param {string} [emptyMessage] - shown when `document` is null.
 */
const DocumentDetailsPanel = ({
  document,
  excludeLink,
  title = "File Details",
  emptyMessage = "Select a document to see details.",
}) => {
  const links = document
    ? (document.links ?? []).filter((link) => !excludeLink?.(link))
    : [];

  return (
    <>
      <Typography
        variant="h6"
        align="center"
        gutterBottom
        sx={{ fontWeight: 600, color: "primary.main", mb: 2 }}
      >
        {title}
      </Typography>

      {document ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, px: 1 }}>
          <Box>
            <SectionLabel>File Info</SectionLabel>
            <DetailItem label="Name" value={document.name} />
            <DetailItem label="Revision" value={document.revNumber || "N/A"} />
            <DetailItem
              label="Document Type"
              value={document.documentType?.name || "N/A"}
            />
          </Box>

          <Divider />

          <Box>
            <SectionLabel>Related Assets</SectionLabel>
            <DetailItem
              value={
                links.length ? (
                  <Stack spacing={1} divider={<Divider />}>
                    {links.map((link) => (
                      <Box key={link.id}>
                        <Typography variant="body2">
                          {link.parent_name || `#${link.parent_id}`}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                          {link.parent_type}
                          {link.plant_name ? ` · ${link.plant_name}` : ""}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  "Not linked to any assets"
                )
              }
            />
          </Box>

          <Divider />

          <Box>
            <SectionLabel>Timeline</SectionLabel>
            <DetailItem label="Created" value={document.created} />
            <DetailItem label="Modified" value={document.modified} />
          </Box>

          <Divider />

          <Box>
            <SectionLabel>Author & Description</SectionLabel>
            <DetailItem label="Created By" value={document.created_by} />
            <DetailItem
              label="Description"
              value={document.description || "N/A"}
            />
          </Box>
        </Box>
      ) : (
        <Typography
          align="center"
          color="text.secondary"
          sx={{ marginTop: 4, fontStyle: "italic" }}
        >
          {emptyMessage}
        </Typography>
      )}
    </>
  );
};

export default DocumentDetailsPanel;
